import { useState } from "react";
import { theme, inputStyle } from "../theme";

/**
 * Picks an AI model — the one place a model is chosen (My Organisation → AI Provider, consultant
 * AI Provider). On an organisation's or a consultancy's own key any model may be named
 * (mode "own"); on the platform's key an organisation picks for its compliance checks from the
 * models the platform prices, billed per use at that model's rate (mode "platform"; the closed
 * list is backend lib/aiModels.ts PLATFORM_COMPLIANCE_MODELS, which the server enforces).
 */

export type ModelProvider = "anthropic" | "openai" | "azure_openai";

interface ModelChoice {
  /** Stored as aiModel; null = leave the choice to the platform default for the provider. */
  id: string | null;
  label: string;
  note: string;
}

const MODEL_CHOICES: Record<"anthropic" | "openai", ModelChoice[]> = {
  anthropic: [
    { id: null,                label: "Recommended",       note: "Abacas chooses for each task: Sonnet 5.5 for compliance checks." },
    { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5", note: "Strong judgement over long evidence at a moderate price. 1M-token window." },
    { id: "claude-opus-5-5",   label: "Claude Opus 5.5",   note: "The most thorough assessment, at about twice Sonnet's price. 1M-token window." },
    { id: "claude-haiku-4-5",  label: "Claude Haiku 4.5",  note: "Fastest and cheapest. Smaller 200K window, so large standards take more passes." },
  ],
  openai: [
    { id: null,          label: "Recommended", note: "GPT-4o." },
    { id: "gpt-4o",      label: "GPT-4o",      note: "OpenAI's general model." },
    { id: "gpt-4o-mini", label: "GPT-4o mini", note: "Faster and cheaper; less thorough on long evidence." },
  ],
};

export function ModelPicker({ provider, value, onChange, disabled = false, mode = "own" }: {
  provider: ModelProvider;
  value: string | null;
  onChange: (model: string | null) => void;
  disabled?: boolean;
  mode?: "own" | "platform";
}) {
  const platform = mode === "platform";
  // Azure OpenAI names a deployment, not a model: there is nothing to pick from, only a name to type.
  const choices = provider === "azure_openai" ? [] : MODEL_CHOICES[provider];
  const known = choices.some((c) => c.id === value);
  const [other, setOther] = useState(!platform && !known && value !== null);
  const showInput = provider === "azure_openai" || other;

  const selected = choices.find((c) => !other && c.id === value);

  return (
    <div>
      {choices.length > 0 && (
        <div role="radiogroup" aria-label="Model" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {choices.map((c) => (
            <ChoiceButton
              key={c.id ?? "default"}
              label={c.label}
              active={!other && c.id === value}
              disabled={disabled}
              onClick={() => { setOther(false); onChange(c.id); }}
            />
          ))}
          {!platform && (
            <ChoiceButton
              label="Other…"
              active={other}
              disabled={disabled}
              onClick={() => { setOther(true); if (known) onChange(null); }}
            />
          )}
        </div>
      )}

      {selected && (
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 8, lineHeight: 1.5 }}>{selected.note}</div>
      )}

      {showInput && (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value.trim() || null)}
          disabled={disabled}
          placeholder={provider === "azure_openai" ? "Your deployment name, e.g. gpt-4o" : "Model ID, e.g. claude-opus-5"}
          aria-label={provider === "azure_openai" ? "Deployment name" : "Model ID"}
          style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginTop: choices.length > 0 ? 10 : 0, fontFamily: "monospace", fontSize: 12 }}
        />
      )}

      <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8, lineHeight: 1.5 }}>
        {platform
          ? "Used for your compliance checks and billed per use at the chosen model’s own rate. Other AI tasks run on the model Abacas chooses."
          : "Used for every AI task on this key: compliance checks, policy writing and guidance."}
      </div>
    </div>
  );
}

function ChoiceButton({ label, active, disabled, onClick }: {
  label: string; active: boolean; disabled: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      style={{
        fontSize: 12, padding: "6px 12px", borderRadius: 3, cursor: disabled ? "default" : "pointer",
        background: active ? theme.color.accentBg : theme.color.panelAlt,
        border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
        color: active ? theme.color.accentText : theme.color.subtle,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}
